import { exploreSchemaItemList } from '@/templates/Landing/seoLandings/exploreSchema';
import { ENV } from '@/constants';

export const schemaObject_AiFilters = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-filters',
			name: 'AI Photo Filters — Filter App for Photos Online | Lift',
			description:
				"Apply stunning AI photo filters instantly with Lift's filter app. Transform images with smart effects, artistic styles & pro edits. No watermark. Try it now online.",
			url: 'https://liftapp.ai/ai-filters',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/ai-filters#product' },
				{ '@id': 'https://liftapp.ai/ai-filters#howto' },
				{ '@id': 'https://liftapp.ai/ai-filters#benefits' },
				{ '@id': 'https://liftapp.ai/ai-filters#faq' },
				{ '@id': 'https://liftapp.ai/ai-filters#why-lift' },
				{ '@id': 'https://liftapp.ai/ai-filters#explore-tools' },
				{ '@id': 'https://liftapp.ai/ai-filters#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-filters#product',
			name: 'AI Filters for Photos Online',
			description:
				"Transform your photos instantly with Lift's AI-powered filters. Apply stunning effects, artistic styles, and smart enhancements in seconds",
			url: 'https://liftapp.ai/ai-filters',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-filters#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Choose your AI filter',
					text: 'Browse dozens of smart filters — from artistic styles to photo effects',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-filters#benefits',
			name: 'Who can benefit from AI Photo Filters?',
			description:
				"Lift's AI Photo Filters are useful for creators, social media users, business owners, and content creators who want to transform photos into stunning artwork.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Social media creators boosting their feed aesthetics',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Influencers crafting cohesive visual styles in seconds',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Small businesses designing branded stories and ads',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Photographers experimenting with fresh, AI-made moods',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Marketing teams needing fast campaign-ready visuals',
				},
				{
					'@type': 'ListItem',
					position: 6,
					name: 'Event planners or bloggers polishing recaps and galleries',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'AI Photo Filters by Lift',
				description:
					'Example of how Lift AI Photo Filters transform photos into stunning artwork.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-filters#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What are Lift AI Photo Filters?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Photo Filters are online tools that transform your photos into stunning AI-generated artwork — perfect for social media, prints, digital galleries, or creative projects.',
					},
				},
				{
					'@type': 'Question',
					name: 'How do Lift AI Photo Filters work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift uses advanced AI and deep learning to analyze your photo, preserve facial details, and reimagine it in unique artistic styles. Everything happens automatically — no editing skills required.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I transform any photo with AI Filters?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Yes! Lift works with portraits, landscapes, pets, and any kind of image. The AI adapts to your photo's composition and applies creative filters that enhance color, lighting, and artistic feel while keeping your original look intact.",
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download my AI-edited photos without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Absolutely. You can export your AI-generated images without a watermark with a Pro plan. Downloads are available in high-resolution PNG or JPG — perfect for social media, printing, or marketing use.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-filters#why-lift',
			name: "Why Lift's AI Photo Filters stands out",
			description:
				"Lift's AI Photo Filters combine professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Why use Lift AI Photo Filters',
				description:
					"Comparison of Lift's AI Photo Filters benefits — high-quality results, intelligent rendering, multi-platform access, and cost savings.",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-filters#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-filters#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Photo Filters',
					item: 'https://liftapp.ai/ai-filters',
				},
			],
		},
	],
};
